import {
  Box,
  Flex,
  Spinner,
  Text,
  IconButton,
  HStack,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { useProjectsContext } from "@root/context/projects-context";
import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { getExtractionReviewById } from "@root/store/thunks/documentsActions";
import SingleLoader from "./SingleLoader";
import { LongLeftArrow } from "@root/assets/icons";
import data from "@root/assets/synonyms.json";
import {
  DOCUMENT_STATUS,
  FILE_TYPE,
  FINISHING_MATERIAL_STATUS,
  ROUTE_NAMES,
} from "@root/constants";
import { CommonButton } from "@root/components/common/button";
import RefreshIcon from "@root/assets/icons/history-refresh-icon";
import { ViewHistoryDrawer } from "./ViewHistoryDrawer";
import { GetChatbotName } from "@root/utils";
import SynonymDrawer from "@root/components/synonym-drawer/synonym-drawer";
import { useMainChatContext } from "@root/context/kps-chat-context";

const SingleDocument = () => {
  const { t } = useTranslation("common");
  const [loading, setLoading] = useState(true);
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const dispatch = useDispatch();
  const { fetchDocument, documentData, setDocumentData } = useProjectsContext();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const status = documentData?.status;
  const type = documentData?.type;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const isExtractionReview = pathname.includes(ROUTE_NAMES.EXTRACTION_REVIEW);

  const {
    isOpen: isSynonymOpen,
    onOpen: onSynonymOpen,
    onClose: onSynonymClose,
  } = useDisclosure();

  // History Drawer controls
  const {
    isOpen: isHistoryOpen,
    onOpen: onHistoryOpen,
    onClose: onHistoryClose,
  } = useDisclosure();

  const getStatusConstants = () => {
    // if (documentData?.type === FILE_TYPE.FINISHING_MATERIAL_REQUEST) {
    //   return FINISHING_MATERIAL_STATUS;
    // }
    return DOCUMENT_STATUS;
  };

  const DOC_STATUS = {
    ...getStatusConstants(),
    isProcessing: () =>
      DOC_STATUS.PROCESSING.includes(status) ||
      (status === DOC_STATUS.COMPLETED && !documentData?.googleSheetsInfo) ||
      !documentData,
    hasError: () => status === DOC_STATUS.ERROR,
    isCompleted: () =>
      status === DOC_STATUS.COMPLETED && documentData?.googleSheetsInfo,
  };

  useEffect(() => {
    if (!id) return;

    const pollDocument = () => {
      if (isExtractionReview) {
        dispatch(
          getExtractionReviewById({
            id,
            onSuccess: (response: any) => {
              // The thunk's onSuccess receives the data from prepareResponseObject
              // which has structure: { data: ..., error: ..., statusCode: ... }
              // fetchDocument sets: setDocumentData(res?.data?.data)
              // So we extract the data property to match the same structure
              setDocumentData(response?.data || response);
            },
            onError: () => {
              console.error("Failed to fetch extraction review");
            },
          })
        );
      } else {
        fetchDocument(id);
      }
    };

    // Initial fetch
    pollDocument();

    // Start polling
    intervalRef.current = setInterval(pollDocument, 4000);

    // Cleanup interval on unmount or when dependencies change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setDocumentData(null);
    };
  }, [id]);

  useEffect(() => {
    if (DOC_STATUS.hasError() || DOC_STATUS.isCompleted()) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  }, [status, documentData?.googleSheetsInfo]);

  const getEmbedUrl = () => {
    if (documentData?.googleSheetsInfo?.spreadsheetId) {
      return `https://docs.google.com/spreadsheets/d/${documentData?.googleSheetsInfo?.spreadsheetId}/edit?usp=embed`;
    }
    return "";
  };

  const getLoaderText = () => {
    if (status === 1) {
      return t("single_document.generatingAnalysisResults");
    }
    if (status === 2) {
      return t("single_document.downloadFile");
    }
    if (status === 10) {
      return t("single_document.readingDataFromFile");
    }
    if (status === 3) {
      return t("single_document.classifyingTablesInPages");
    }
    if (status === 4) {
      return t("single_document.extractingDataFromTables");
    }
    if (status === 5) {
      return t("single_document.populatingDataIntoExcel");
    }
    if (status === 6 || status === 7 || status === 8) {
      return t("single_document.gettingExcelSheetReady");
    }
    return t("single_document.loading");
  };

  const getLoaderProgress = () => {
    switch (status) {
      case 1:
        return 10;
      case 2:
        return 30;
      case 10:
        return 40;
      case 3:
        return type === FILE_TYPE.FINISHING_MATERIAL_REQUEST ? 60 : 50;
      case 4:
        return type === FILE_TYPE.FINISHING_MATERIAL_REQUEST ? 30 : 60;
      case 5:
        return type === FILE_TYPE.FINISHING_MATERIAL_REQUEST ? 60 : 70;
      case 6:
        return 80;
      case 7:
        return 90;
      case 8:
        return 95;
      default:
        return 0;
    }
  };

  if (DOC_STATUS.isProcessing()) {
    return (
      <SingleLoader text={getLoaderText()} progress={getLoaderProgress()} />
    );
  }

  if (DOC_STATUS.hasError()) {
    return (
      <Flex height={"100%"} justifyContent={"center"} alignItems={"center"}>
        <Text
          fontSize={"14px"}
          textAlign={"center"}
          color={"#5C6670"}
          marginTop={"10px"}
        >
          {t("single_document.failedToProcessDocument")}
        </Text>
      </Flex>
    );
  }

  function renderHeaderTitle() {
    if (documentData?.type === FILE_TYPE.EXCEL_AUTO_COMPLETE) {
      return t("translate.legalReview").toLowerCase();
    }
    if (documentData?.type === FILE_TYPE.FINISHING_MATERIAL_REQUEST) {
      return t("sidebar.materialComparisonAnalysis");
    }
    if (documentData?.files?.length) {
      return t("reports.permitComplianceVerification");
    }
    return GetChatbotName(documentData?.type, t);
  }

  return (
    <Flex height={"100%"} flexDir={"column"}>
      {/* <Box>
        <Flex alignItems={"center"} gap={"16px"} marginBottom={"8px"}>
          <IconButton
            aria-label={t("projects.back")}
            icon={<LongLeftArrow size={28} color="#5C6670" />}
            variant="ghost"
            size="sm"
            onClick={handleBackNavigation}
            _hover={{
              backgroundColor: "#F5F5F5",
              transform: "scale(1.05)",
            }}
            _active={{
              backgroundColor: "#E5E5E5",
              transform: "scale(0.95)",
            }}
            transition="all 0.2s ease"
            borderRadius="8px"
            minW="44px"
            h="44px"
          />
          <Text fontWeight={700} fontSize={"26px"} color={"#15161A"}>
            {t("single_document.implementationAnalysisResults")}
          </Text>
        </Flex>
        <Text color={"#5C6670"} paddingTop={"10px"}>
          {t("single_document.uploadPermitFilesDescription")}
        </Text>
      </Box> */}

      <HStack
        borderBottom={"1px solid #CACACA"}
        width={"100%"}
        p={"15px 30px"}
        justifyContent={"space-between"}
      >
        <Box
          as="button"
          onClick={() => navigate(-1)}
          display="flex"
          alignItems="center"
          gap="8px"
          color="#1A1A1A"
          fontSize="16px"
          fontWeight={400}
          bg="transparent"
          border="none"
          cursor="pointer"
          _hover={{
            opacity: 0.7,
          }}
          transition="opacity 0.2s"
        >
          <Text as="span">←</Text>
          <Text as="span" fontWeight={500} color={"#0E0E0E"}>
            {t("translate.goBack")}
          </Text>
        </Box>
        <Flex gap={"25px"}>
          {id &&
            documentData?.type === FILE_TYPE.FINISHING_MATERIAL_REQUEST && (
              <CommonButton
                variant="primary"
                onClick={onSynonymOpen}
                bg="#EBEBEB"
                color="#1A1A1A"
                _hover={{ bg: "#dbd5d5ff" }} // <-- lighter hover
              >
                {t("synonymDrawer.synonymDictionary")}
              </CommonButton>
            )}
          {/* {id && (
            <CommonButton
              variant="primary"
              startIcon={<RefreshIcon color="#fff" />}
              onClick={onHistoryOpen}
            >
              {t("translate.viewHistory")}
            </CommonButton>
          )} */}
        </Flex>
      </HStack>

      <Box flex={1} padding={"15px 30px"} width={"100%"}>
        {loading && (
          <Flex height={"100%"} alignItems={"center"} justifyContent={"center"}>
            <Box textAlign={"center"}>
              <Spinner size={"xl"} />
              <Text
                fontSize={"14px"}
                textAlign={"center"}
                color={"#5C6670"}
                marginTop={"10px"}
              >
                {t("single_document.loadingExcelSheet")}
              </Text>
            </Box>
          </Flex>
        )}
        <Flex flexDirection={"column"} height={"100%"}>
          {!loading && (
            <Text
              paddingBottom={"10px"}
              fontWeight={700}
              color={"#15161A"}
              fontSize={"26px"}
              textTransform={"capitalize"}
            >
              {renderHeaderTitle()}
            </Text>
          )}
          <Box flex={1}>
            <iframe
              src={getEmbedUrl()}
              width={"100%"}
              height={"100%"}
              frameBorder="0"
              title={t("single_document.googleSpreadsheet")}
              allowFullScreen
              onLoad={() => setLoading(false)}
              style={{ display: loading ? "none" : "block" }}
            />
          </Box>
        </Flex>
      </Box>
      {/* 👉 Synonym Drawer */}
      <SynonymDrawer
        isOpen={isSynonymOpen}
        onClose={onSynonymClose}
        data={data}
      />

      {/* <ViewHistoryDrawer
        isOpen={isHistoryOpen}
        onClose={onHistoryClose}
        type={documentData?.type}
      /> */}
    </Flex>
  );
};

export default SingleDocument;
